import type { WidgetSettings } from './types'

/**
 * Styles for the Shadow DOM. Injected as a single sheet so the WordPress theme cannot reach
 * in and nothing here leaks out.
 *
 * Contrast, from the brand palette: gold #FFB81C on white is 1.73:1 and fails WCAG badly, so
 * gold is only ever a background or a border. Text on gold is navy (4.53:1, AA); navy on
 * white is 7.85:1 (AAA). Do not set gold as a text colour anywhere in here.
 */
export function styles(s: WidgetSettings): string {
  const navy = s.colour_navy
  const gold = s.colour_gold
  const isLeft = s.position.endsWith('left')

  return `
:host {
  --navy: ${navy};
  --gold: ${gold};
  --ink: #1b1b1b;
  --muted: #5c6670;
  --line: #dfe3e6;
  --bg: #ffffff;
  --bubble: #f2f5f7;

  all: initial;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.5;
}

*, *::before, *::after { box-sizing: border-box; }

.root {
  position: fixed;
  ${isLeft ? 'left' : 'right'}: ${s.offset_x}px;
  bottom: ${s.offset_y}px;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  align-items: ${isLeft ? 'flex-start' : 'flex-end'};
  gap: 12px;
}

/* ---------------------------------------------------------------- launcher */

/*
 * The launcher reproduces the supplied artwork: a gold-outlined speech bubble with a tail at
 * the corner nearest the screen edge, the heart at its left, and the text over two lines.
 *
 * The bubble is a bordered element rather than an image, so it grows with whatever the
 * launcher text is set to and the text stays real text — selectable, scalable, and readable
 * by a screen reader. Only the tail is drawn.
 */
.launcher {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 11px;
  padding: 13px 22px 13px 16px;
  /* Room for the tail, which hangs below the bubble. */
  margin-bottom: 18px;
  background: var(--bg);
  border: 4px solid var(--gold);
  border-radius: 26px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 0, 0, .16);
  color: var(--navy);
  font-size: 17px;
  font-weight: 800;
  transition: transform .18s ease, box-shadow .18s ease;
}

.launcher:hover { transform: translateY(-2px); box-shadow: 0 10px 26px rgba(0, 0, 0, .2); }
.launcher:focus-visible { outline: 3px solid var(--navy); outline-offset: 3px; }
.launcher[hidden] { display: none; }

/*
 * Two lines, as in the artwork. text-wrap: balance splits "Ask me anything!" after "me";
 * where it is unsupported the max-width alone wraps it in the same place, and any longer
 * wording set by staff wraps to as many lines as it needs rather than being truncated.
 */
.launcher-text {
  max-width: 9ch;
  text-align: left;
  line-height: 1.12;
  text-wrap: balance;
}

.launcher .heart { flex: 0 0 auto; width: 44px; height: 44px; }

/*
 * The tail sits on the bubble's bottom edge, overlapping it by the border width so its own
 * stroke meets the border and the white bar inside it covers the border across the mouth.
 */
.launcher .tail {
  position: absolute;
  /* top: 100% puts the SVG's own y=0 exactly on the top edge of the bubble's bottom border,
     which is what lets the white bar inside it erase that border across the tail's mouth.
     Anchoring from the bottom instead lands the bar in the content box, leaving the border
     drawn straight across the tail. */
  top: 100%;
  ${isLeft ? 'left' : 'right'}: 30px;
  width: 32px;
  height: 20px;
  pointer-events: none;
}

/*
 * Pulse draws the eye on a first visit. It runs a fixed number of times rather than
 * forever — a permanently animating element on a page someone is trying to read is a
 * nuisance, and for some visitors a genuine accessibility problem.
 */
@keyframes gvfb-pulse {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.14); }
}

.launcher.pulsing .heart {
  animation: gvfb-pulse 1.1s ease-in-out var(--pulse-count, 3);
  transform-origin: center;
}

@media (prefers-reduced-motion: reduce) {
  .launcher, .panel { transition: none; }
  .launcher.pulsing .heart { animation: none; }
}

/* ------------------------------------------------------------------- panel */

.panel {
  width: min(400px, calc(100vw - 32px));
  height: min(620px, calc(100vh - 120px));
  display: flex;
  flex-direction: column;
  background: var(--bg);
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, .26);
  overflow: hidden;
}

.panel[hidden] { display: none; }

.header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: var(--navy);
  color: #fff;
}

.header .heart { width: 30px; height: 30px; flex: 0 0 auto; }
.header h2 { margin: 0; font-size: 17px; font-weight: 700; flex: 1 1 auto; color: #fff; }

.icon-button {
  appearance: none;
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: inline-flex;
  line-height: 0;
}

.icon-button:hover { background: rgba(255, 255, 255, .16); }
.icon-button:focus-visible { outline: 3px solid var(--gold); outline-offset: 2px; }

/*
 * The bar holds the Back button and nothing else. It sits to the right on the food bank's
 * feedback: on the left it reads as part of the header above it and gets overlooked, while
 * on the right it lands where the eye already is after reading an answer, and near the
 * scrollbar and the send button rather than across the panel from them.
 */
.breadcrumb {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--line);
  background: #fafbfc;
  font-size: 13px;
  color: var(--muted);
}

.breadcrumb[hidden] { display: none; }

.back {
  appearance: none;
  border: 1px solid var(--line);
  background: #fff;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--navy);
  cursor: pointer;
}

.back:hover { border-color: var(--navy); }
.back:focus-visible { outline: 3px solid var(--navy); outline-offset: 2px; }

.log {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.msg { max-width: 92%; }
.msg.bot { align-self: flex-start; }
.msg.user { align-self: flex-end; }

.bubble {
  padding: 11px 14px;
  border-radius: 14px;
  background: var(--bubble);
  color: var(--ink);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.msg.user .bubble { background: var(--navy); color: #fff; }

.notice {
  font-size: 13.5px;
  color: var(--muted);
  background: #fafbfc;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 10px 12px;
}

/* Crisis card: gold band for attention, navy text for contrast. */
.crisis {
  border: 2px solid var(--gold);
  border-left-width: 6px;
  border-radius: 12px;
  padding: 12px 14px;
  background: #fffaf0;
}

.crisis p { margin: 0 0 8px; font-weight: 700; color: var(--navy); }
.crisis a { display: block; margin-top: 6px; color: var(--navy); font-weight: 600; }

.chips { display: flex; flex-wrap: wrap; gap: 8px; }

.chip {
  appearance: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--navy);
  background: #fff;
  border: 2px solid var(--line);
  border-radius: 999px;
  padding: 9px 15px;
  transition: border-color .15s ease, background .15s ease;
}

.chip:hover { border-color: var(--navy); background: #f5f9fc; }
.chip:focus-visible { outline: 3px solid var(--navy); outline-offset: 2px; }

/*
 * A row of options that has been used stays in the log, so the transcript still shows what
 * was on offer — and so Back has something to switch back on. Only the affordance goes; the
 * navy text stays at full strength, because this is a record of the conversation rather than
 * a greyed-out control, and dimming it would put it under AA.
 */
.chip:disabled { cursor: default; background: #f6f8fa; }
.chip:disabled:hover { border-color: var(--line); background: #f6f8fa; }

/* Links are visually distinct from navigation chips: these leave the widget. */
.chip.link { border-color: var(--gold); background: #fffdf5; }
.chip.link:hover { background: #fff8e6; border-color: var(--navy); }

.links { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }

.stars { display: flex; gap: 6px; }

.star {
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 26px;
  line-height: 1;
  padding: 2px;
  color: var(--gold);
  border-radius: 6px;
}

.star:focus-visible { outline: 3px solid var(--navy); outline-offset: 2px; }

.typing { display: inline-flex; gap: 4px; padding: 12px 14px; }

.typing span {
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--muted);
  animation: gvfb-blink 1.2s infinite ease-in-out;
}

.typing span:nth-child(2) { animation-delay: .18s; }
.typing span:nth-child(3) { animation-delay: .36s; }

@keyframes gvfb-blink { 0%, 80%, 100% { opacity: .25; } 40% { opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .typing span { animation: none; opacity: .55; }
}

.composer {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid var(--line);
  background: #fff;
}

.composer[hidden] { display: none; }

.composer input {
  flex: 1 1 auto;
  font: inherit;
  padding: 10px 13px;
  border: 2px solid var(--line);
  border-radius: 999px;
  color: var(--ink);
  background: #fff;
  min-width: 0;
}

.composer input:focus { outline: none; border-color: var(--navy); }

.send {
  appearance: none;
  border: 0;
  border-radius: 50%;
  width: 42px; height: 42px;
  flex: 0 0 auto;
  background: var(--navy);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.send:disabled { opacity: .45; cursor: default; }
.send:focus-visible { outline: 3px solid var(--gold); outline-offset: 2px; }

.footer {
  padding: 7px 14px 10px;
  font-size: 11.5px;
  color: var(--muted);
  text-align: center;
  background: #fff;
}

.footer a { color: var(--muted); }

/* Screen-reader-only, for the live region and control labels. */
.sr {
  position: absolute;
  width: 1px; height: 1px;
  margin: -1px; padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

/* On a small screen the panel becomes a sheet — a 400px card floating in a 360px
   viewport is unusable, and this is a mobile-heavy audience. */
@media (max-width: 480px) {
  .root { left: 0; right: 0; bottom: 0; align-items: stretch; gap: 0; }
  .launcher { margin: 0 12px 12px auto; }
  .panel {
    width: 100vw;
    height: 100dvh;
    border-radius: 0;
  }
}
`
}
